import { AxiosInstance } from 'axios';
import client from './RestClient';
import {
  BeaconMessageDTO,
  BeaconMessageFormType,
  BeaconMessagesWithPagination,
  BeaconConnectionOption
} from 'core/beaconMessage/BeaconMessage';
import { Pageable } from 'ws/Pageable';
import { LineMessage, LineMessageType } from 'core/advertiser/LineBeacon';

export interface BeaconMessageWebService {
  getBeaconConnectionOptions(): Promise<BeaconConnectionOption[]>;
  getBeaconMessages(
    agencyId: number | string | undefined,
    beaconConnectionId: number | string | undefined,
    pageable: Pageable,
    search?: string
  ): Promise<BeaconMessagesWithPagination>;
  createBeaconMessage(beaconMessage: BeaconMessageDTO): Promise<void>;
  updateBeaconMessage(
    l2ObjectId: number | string,
    beaconMessage: BeaconMessageDTO
  ): Promise<void>;
  getBeaconMessage(l2ObjectId: number | string): Promise<BeaconMessageFormType>;
  updateBeaconMessagesState(
    beaconMessageIds: number[],
    state: 'activate' | 'deactivate'
  ): Promise<void>;
}

export class RestfulBeaconMessageWebService implements BeaconMessageWebService {
  constructor(private restClient: AxiosInstance = client) {}

  async getBeaconConnectionOptions(): Promise<BeaconConnectionOption[]> {
    const response = await this.restClient.get(
      '/v2/ads-beacon/line-beacon-connection-options'
    );
    return response.data.records;
  }

  async getBeaconMessages(
    agencyId: number | string | undefined,
    beaconConnectionId: number | string | undefined,
    pageable: Pageable,
    search?: string
  ): Promise<BeaconMessagesWithPagination> {
    const { page, sizePerPage, sort, direction } = pageable;
    let url = `/v2/ads-beacon?page=${page}&size=${sizePerPage}&sort=${sort},${direction}&search=${search || ''}`;
    if (agencyId) {
      url += `&agencyId=${agencyId}`;
    }
    if (beaconConnectionId) {
      url += `&beaconConnectionId=${beaconConnectionId}`;
    }
    const response = await this.restClient.get(url);
    return {
      pagination: response.data.pagination,
      beaconMessages: response.data.records
    };
  }

  async createBeaconMessage(beaconMessage: BeaconMessageDTO): Promise<void> {
    await this.restClient.post('/v2/ads-beacon', beaconMessage);
  }

  async updateBeaconMessage(
    l2ObjectId: number | string,
    beaconMessage: BeaconMessageDTO
  ): Promise<void> {
    await this.restClient.put('/v2/ads-beacon', {
      ...beaconMessage,
      l2ObjectId
    });
  }

  async getBeaconMessage(
    l2ObjectId: number | string
  ): Promise<BeaconMessageFormType> {
    const response = await this.restClient.get(`/v2/ads-beacon/${l2ObjectId}`);
    const beaconConnectionOptions = await this.getBeaconConnectionOptions();
    return this.wrapBeaconMessage(response.data, beaconConnectionOptions);
  }

  async updateBeaconMessagesState(
    beaconMessageIds: number[],
    state: 'activate' | 'deactivate'
  ): Promise<void> {
    await this.restClient.put(`/v2/ads-beacon/${state}`, beaconMessageIds);
  }

  private wrapBeaconMessage(
    data: any,
    beaconConnectionOptions: BeaconConnectionOption[]
  ): BeaconMessageFormType {
    const creativeValues = JSON.parse(data.creativeValues);
    const beaconConnection = beaconConnectionOptions.find(
      (option) => option.value === creativeValues.beaconConnectionId
    );
    const storesOptions =
      (data.limitations.find((limitation) => limitation.type === 'storeNum')
        ?.limits as SelectOptions[]) || [];
    const stores = storesOptions.map((option) => option.value.toString());
    return {
      name: data.name,
      startDate: data.startDate,
      endDate: data.endDate,
      dayPart: data.dayPart,
      channel: data.channel,
      channelId: data.channelId,
      adTypeId: data.adTypeId,
      creative: {
        creativeType: data.adTypeId,
        typeProperties: {
          stores,
          beaconConnection,
          maxFrequency: creativeValues.maxFrequency,
          intervalHours: creativeValues.intervalHours,
          beaconType: creativeValues.beaconType
        },
        lineMessage: creativeValues.lineMessage.map(
          (lineMessage: LineMessage) => ({
            type: lineMessage.type,
            message:
              lineMessage.type === LineMessageType.TEXT
                ? lineMessage.message
                : undefined,
            image:
              lineMessage.type === LineMessageType.IMAGE
                ? {
                    url: lineMessage.imageUrl
                  }
                : undefined,
            video:
              lineMessage.type === LineMessageType.VIDEO
                ? {
                    url: lineMessage.videoUrl
                  }
                : undefined,
            previewImage:
              lineMessage.type === LineMessageType.VIDEO
                ? {
                    url: lineMessage.previewImageUrl
                  }
                : undefined,
            officialAccount:
              lineMessage.type === LineMessageType.JOIN
                ? lineMessage.officialAccount
                : undefined,
            couponId:
              lineMessage.type === LineMessageType.COUPON
                ? lineMessage.couponId
                : undefined
          })
        )
      }
    };
  }
}
